import SDKConfigurationModel from "../../../Configurator/Models/SDKConfiguraitonModel";
import LoyaltyHttps from "../../../Https/LoyaltyHttps";
import PrivateLeaguesHttps from "../../../Https/PrivateLeaguesHttps";
import PrivateLeaguesFactory from "../../PrivateLeagues/Factory/PrivateLeaguesFactory";
import InvitationFilters from "../../PrivateLeagues/Models/Filters/InvitationFilters";
import PrivateLeaguePostsFilters from "../../PrivateLeagues/Models/Filters/PrivateLeaguePostsFilters";
import { ErrorCodes } from "../../../Exception/ErrorCodes";
import { ErrorStatuses } from "../../../Exception/ErrorStatuses";
import StandardFansUnitedException from "../../../Exception/StandardFansUnitedException";
import { ErrorMessages } from "../../../Global/Messages/Messages";
import PrivateLeaguePredictionsFilters from "../../PrivateLeagues/Models/Filters/PrivateLeaguePredictionsFilters";
import PrivateLeagueParamCreateBody from "../../PrivateLeagues/Models/PrivateLeagueParamCreateBody";
import PrivateLeagueParamUpdateBody from "../../PrivateLeagues/Models/PrivateLeagueParamUpdateBody";
import PrivateLeagueService from "../../PrivateLeagues/Service/PrivateLeagueService";
import Profile from "../../Profile/Profile";
import AcceptChallengeModel from "../Models/AcceptChallengeModel";
import ChallengeFilters from "../Models/Filters/ChallengeFilters";
import DiscussionsService from "../../Discussions/Service/DiscussionsService";
import PostByIdFullModel from "../../Discussions/Models/PostByIdFullModel";
import MyChallengesFilters from "../Models/Filters/MyChallengesFilters";

export default class ChallengesFacade {
  private factory: PrivateLeaguesFactory = null;
  private https: PrivateLeaguesHttps = null;
  private loyaltyHttps: LoyaltyHttps = null;
  private profile: Profile = null;
  private service: PrivateLeagueService = null;
  private discussionsService: DiscussionsService = null;

  constructor(config: SDKConfigurationModel) {
    this.factory = new PrivateLeaguesFactory();
    this.https = new PrivateLeaguesHttps(config);
    this.service = new PrivateLeagueService(config);
    this.loyaltyHttps = new LoyaltyHttps(config);
    this.profile = new Profile(config);
    this.discussionsService = new DiscussionsService(config);
  }

  public issue = async (body: PrivateLeagueParamCreateBody, userId: string) => {
    const requestBody = this.factory.createRequestBodyFromParam(
      body,
      "ONE_VS_ONE"
    );
    const challenge = await this.https.create(requestBody, "CHALLENGE");
    await this.service.setTemplateModels(challenge);
    const invitedUsers = await this.https.invite(challenge.id, [userId]);
    challenge.invites = [];
    challenge.invites.push(invitedUsers[0]);

    return challenge;
  };

  public update = async (
    challengeId: string,
    body: PrivateLeagueParamUpdateBody
  ) => {
    const requestBody = this.factory.createUpdateRequestBodyFromParam(
      body,
      "ONE_VS_ONE"
    );
    const challenge = await this.https.update(
      challengeId,
      requestBody,
      "CHALLENGE"
    );
    await this.service.setTemplateModels(challenge);

    return challenge;
  };

  public updateTemplate = async (
    challengeId: string,
    newTemplateId: string,
    oldTemplateName: string
  ) => {
    const challenge = await this.https.updateLeagueTemplate(
      challengeId,
      newTemplateId,
      oldTemplateName,
      "CHALLENGE"
    );
    await this.service.setTemplateModels(challenge);

    return challenge;
  };

  public delete = async (leagueId: string) => {
    return await this.https.delete(leagueId, "CHALLENGE");
  };

  public getById = async (challengeId: string, disableCache?: boolean) => {
    const challenge = await this.https.getById(challengeId, "CHALLENGE", disableCache);
    await this.service.setTemplateModels(challenge);

    return challenge;
  };

  public getMyChallenges = async (filters?: MyChallengesFilters) => {
    filters = new MyChallengesFilters(filters);
    const data = await this.https.getMyLeagues(
      filters,
      "CHALLENGE"
    );

    await this.service.setTemplateModelsInLeagues(data);

    return data;
  };

  public accept = async (challengeId: string) => {
    const isAccepting = true;
    const acceptModel = await this.https.accept(
      challengeId,
      isAccepting,
      "CHALLENGE"
    );

    return await this.finalizeAcceptModel(
      acceptModel as AcceptChallengeModel,
      isAccepting
    );
  };

  public reject = async (challengeId: string) => {
    const isAccepting = false;
    const acceptModel = await this.https.accept(
      challengeId,
      isAccepting,
      "CHALLENGE"
    );

    return await this.finalizeAcceptModel(
      acceptModel as AcceptChallengeModel,
      isAccepting
    );
  };

  public leave = async (challengeId: string) => {
    return await this.https.leave(challengeId, "CHALLENGE");
  };

  private finalizeAcceptModel = async (
    acceptModel: AcceptChallengeModel,
    isAccepted: boolean
  ) => {
    // When a challenge is rejected, the user can't get the challenge by ID, because he is not a member
    if (isAccepted) {
      const challengeById = await this.getById(acceptModel.challengeId);
      acceptModel.challengeModel = challengeById;
    }

    await this.service.setProfileModel(acceptModel);

    return acceptModel;
  };

  public getMyChallengesStandings = async (filters: ChallengeFilters) => {
    if (filters) {
      filters = new ChallengeFilters(filters);
    }

    const ownProfile = await this.profile.getOwn().getInfo();
    const challengesRankings =
      await this.loyaltyHttps.getUserRankingsForPrivateLeagueAndChallenges(
        ownProfile.id,
        "CHALLENGE",
        filters
      );

    for (const ranking of challengesRankings.data) {
      const challengeModel = await this.getById(ranking.challengeId);
      ranking.challengeModel = challengeModel;
    }

    this.service.setTemplateModelsFromLeagueModels(
      challengesRankings.data,
      "CHALLENGE"
    );

    return challengesRankings;
  };

  public getInvitations = async (filters?: InvitationFilters) => {
    filters = new InvitationFilters(filters);
    filters.type = "ONE_VS_ONE";
    const data = await this.https.getInvitations("CHALLENGE", filters);
    await this.service.setTemplateModelsInLeagues(data.data);

    return data;
  };

  public getReportedPosts = async (
    challengeId: string,
    filters?: PrivateLeaguePostsFilters
  ) => {
    if (filters) {
      filters = new PrivateLeaguePostsFilters(filters);
    }

    const response = await this.https.getReportedPosts(challengeId, filters);
    response.data = await this.discussionsService.finalizePostFullModel(
      response.data as PostByIdFullModel[]
    );

    return response;
  };

  public getPredictions = async (
    challengeId: string,
    filters?: PrivateLeaguePredictionsFilters
  ) => {
    if (filters) {
      const maxLimit = 20;

      if (filters.limit && filters.limit > maxLimit) {
        // Prediction API is limited with fetching predictions by ids (max ids are 20)
        throw new StandardFansUnitedException(
          ErrorCodes.BAD_METHOD_CALL,
          ErrorStatuses.INVALID_LIMIT,
          ErrorMessages.INVALID_LIMIT + maxLimit.toString()
        );
      }

      filters = new PrivateLeaguePredictionsFilters(filters);
    }

    const response = await this.https.getPredictions(challengeId, filters);

    return await this.service.setModelsForPredictions(response);
  };

  public moderatePost = async (postId: string, moderationReason: string) => {
    return await this.https.moderatePost(postId, moderationReason);
  };
}
